/**
 * New Relic Browser Agent 유틸리티 함수
 */

interface NewRelicWindow extends Window {
  newrelic?: {
    noticeError: (error: Error | string, customAttributes?: Record<string, unknown>) => void
    setCustomAttribute: (name: string, value: string | number | boolean) => void
    setUserId: (userId: string) => void
    addPageAction: (name: string, attributes?: Record<string, unknown>) => void
  }
  NREUM?: {
    ee?: {
      aborted?: boolean
    }
  }
}

/**
 * New Relic에 에러를 전송합니다.
 * @param error - 전송할 에러 객체 또는 에러 메시지
 * @param customAttributes - 추가 커스텀 속성
 */
export function reportErrorToNewRelic(error: Error | string, customAttributes?: Record<string, unknown>): void {
  if (typeof window === 'undefined') {
    return
  }

  const newRelic = (window as NewRelicWindow).newrelic

  if (!newRelic || !newRelic.noticeError) {
    // 초기화되지 않았거나 abort된 경우 조용히 반환
    return
  }

  // New Relic 이벤트 시스템이 abort된 상태인지 확인
  const nreum = (window as NewRelicWindow).NREUM
  if (nreum?.ee?.aborted) {
    // abort된 상태면 에러를 기록하지 않고 조용히 반환
    // (청크 로딩 실패 등으로 인한 abort 상태)
    return
  }

  try {
    if (typeof error === 'string') {
      newRelic.noticeError(new Error(error), customAttributes)
    } else {
      newRelic.noticeError(error, customAttributes)
    }
  } catch (err) {
    // 에러 발생 시에도 조용히 처리 (무한 루프 방지)
    // abort 상태에서 noticeError 호출 시 에러가 발생할 수 있음
  }
}

/**
 * New Relic에 커스텀 속성을 설정합니다.
 * @param name - 속성 이름
 * @param value - 속성 값
 */
export function setNewRelicCustomAttribute(name: string, value: string | number | boolean): void {
  if (typeof window === 'undefined') {
    return
  }

  const newRelic = (window as NewRelicWindow).newrelic

  if (!newRelic || !newRelic.setCustomAttribute) {
    return
  }

  // New Relic 이벤트 시스템이 abort된 상태인지 확인
  const nreum = (window as NewRelicWindow).NREUM
  if (nreum?.ee?.aborted) {
    return
  }

  try {
    newRelic.setCustomAttribute(name, value)
  } catch (err) {
    // 에러 발생 시 조용히 처리
  }
}

/**
 * New Relic에 사용자 ID를 설정합니다.
 * @param userId - 사용자 ID
 */
export function setNewRelicUserId(userId: string): void {
  if (typeof window === 'undefined') {
    return
  }

  const newRelic = (window as NewRelicWindow).newrelic

  if (!newRelic || !newRelic.setUserId) {
    return
  }

  // New Relic 이벤트 시스템이 abort된 상태인지 확인
  const nreum = (window as NewRelicWindow).NREUM
  if (nreum?.ee?.aborted) {
    return
  }

  try {
    newRelic.setUserId(userId)
  } catch (err) {
    // 에러 발생 시 조용히 처리
  }
}

/**
 * New Relic에 페이지 액션을 추가합니다.
 * @param name - 액션 이름
 * @param attributes - 액션 속성
 */
export function addNewRelicPageAction(name: string, attributes?: Record<string, unknown>): void {
  if (typeof window === 'undefined') {
    return
  }

  const newRelic = (window as NewRelicWindow).newrelic

  if (!newRelic || !newRelic.addPageAction) {
    return
  }

  // New Relic 이벤트 시스템이 abort된 상태인지 확인
  const nreum = (window as NewRelicWindow).NREUM
  if (nreum?.ee?.aborted) {
    return
  }

  try {
    newRelic.addPageAction(name, attributes)
  } catch (err) {
    // 에러 발생 시 조용히 처리
  }
}
